import React, { useContext, useEffect, useState } from 'react';
import { getProject, publish, setRemoteRepository } from './api';
import { AuthenticationContext } from './AuthenticationProvider';
import { getTranslation } from '../utility';
import { useTranslation } from 'react-i18next';
import { ProjectResponse } from './types/ProjectResponse';
import { trackPromise } from 'react-promise-tracker';

export type SettingsData = {
  projectId: number;
  projectName: string | undefined;
  remoteRepositoryURL: string | undefined;
  canPublish: boolean;
};

export interface ISettingsContext {
  settingsData: SettingsData | undefined;

  onPublish(_message: string): void;
  onSetRepository(_url: string): void;
}

export interface ISettingsProviderProps {
  projectId: number;
  children: any;
}

const SettingsContext = React.createContext<ISettingsContext>({
  settingsData: undefined,

  onPublish(_message: string) {},
  onSetRepository(_url: string) {},
});

const SettingsProvider = (props: ISettingsProviderProps) => {
  const { children, projectId } = props;

  const { token } = useContext(AuthenticationContext);
  const { i18n, t } = useTranslation();

  const [settingsData, setSettingsData] = useState<SettingsData | undefined>();
  const [fetch, setFetch] = useState(true);

  const language = i18n.language as 'en' | 'fr';

  useEffect(() => {
    if (token && fetch) {
      trackPromise(getProject(projectId, token)).then(
        (data: ProjectResponse) => {
          setSettingsData({
            projectId: projectId,
            projectName: getTranslation(language, data.project.name),
            remoteRepositoryURL: data.project.remote_repository_url,
            canPublish: data.project.can_publish
              ? data.project.can_publish
              : false,
          });
          setFetch(false);
        }
      );
    }
  }, [language, projectId, token, t, fetch]);

  const onPublish = (message: string) => {
    if (token) {
      trackPromise(publish(projectId, message, token))
        .then((data: ProjectResponse) => {
          setSettingsData({
            projectId: projectId,
            projectName: getTranslation(language, data.project.name),
            remoteRepositoryURL: data.project.remote_repository_url,
            canPublish: data.project.can_publish
              ? data.project.can_publish
              : false,
          });
        })
        .catch((err) => console.log(err));
    }
  };

  const onSetRepository = (url: string) => {
    if (token) {
      trackPromise(setRemoteRepository(projectId, url, token))
        .then((data: ProjectResponse) => {
          setSettingsData({
            projectId: projectId,
            projectName: getTranslation(language, data.project.name),
            remoteRepositoryURL: data.project.remote_repository_url,
            canPublish: data.project.can_publish
              ? data.project.can_publish
              : false,
          });
        })
        .catch((err) => console.log(err));
    }
  };

  return (
    <SettingsContext.Provider
      value={{
        settingsData,

        onSetRepository,
        onPublish,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export { SettingsContext, SettingsProvider };
